import React, { Component, ErrorInfo, ReactNode } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { lightTheme } from '../../theme';
import { loggingService } from '../../services/logging-service';
import { log } from '../../services/production-logger';


// Extract colors from theme
const colors = lightTheme.colors;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: string[];
  level?: 'screen' | 'component' | 'critical';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  retryCount: number;
}

/**
 * Enhanced Error Boundary with comprehensive error handling
 * Provides graceful error recovery and detailed error reporting
 */
export class ErrorBoundary extends Component<Props, State> {
  private readonly maxRetries = 3;
  private previousProps: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    };
    this.previousProps = props;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error with context
    this.logError(error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state if specified props change
    if (hasError && resetOnPropsChange) {
      if (!resetKeys) {
        // Reset on any prop change
        this.resetErrorBoundary();
      } else {
        // Reset only if specified keys change
        const hasResetKeyChanged = resetKeys.some(
          key => prevProps[key as keyof Props] !== this.props[key as keyof Props]
        );
        
        if (hasResetKeyChanged) {
          this.resetErrorBoundary();
        }
      }
    }
  }

  private async logError(error: Error, errorInfo: ErrorInfo) {
    try {
      const errorData = {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        errorInfo: {
          componentStack: errorInfo.componentStack,
        },
        context: {
          level: this.props.level || 'component',
          platform: Platform.OS,
          timestamp: new Date().toISOString(),
          retryCount: this.state.retryCount,
        },
        device: {
          platform: Platform.OS,
          version: Platform.Version,
        },
      };

      // Log to service
      await loggingService.logError('error_boundary', error, {
        component: 'ErrorBoundary',
        level: this.props.level || 'component',
        errorInfo,
        retryCount: this.state.retryCount,
      });

      // Store error locally for offline analysis
      await this.storeErrorLocally(errorData);

    } catch (loggingError) {
      log.error('Failed to log error:', loggingError);
    }
  }

  private async storeErrorLocally(errorData: any) {
    try {
      const key = `error_${this.state.errorId}`;
      await AsyncStorage.setItem(key, JSON.stringify(errorData));
      
      // Keep only last 10 errors
      const allKeys = await AsyncStorage.getAllKeys();
      const errorKeys = allKeys.filter(k => k.startsWith('error_'));
      
      if (errorKeys.length > 10) {
        const sortedKeys = errorKeys.sort();
        const keysToRemove = sortedKeys.slice(0, errorKeys.length - 10);
        await AsyncStorage.multiRemove(keysToRemove);
      }
    } catch (error) {
      log.error('Failed to store error locally:', error);
    }
  }

  private resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    });
  };

  private handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    if (newRetryCount <= this.maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: null,
        retryCount: newRetryCount,
      });
    }
  };

  private handleReload = async () => {
    try {
      // Clear any cached data that might be causing issues
      await AsyncStorage.removeItem('cached_data');
      
      // Reset to initial state
      this.resetErrorBoundary();
      
      // If this is a critical error, we might need to restart the app
      if (this.props.level === 'critical') {
        // In a real app, you might use a library like react-native-restart
        log.debug('Critical error - app restart recommended');
      }
    } catch (error) {
      log.error('Failed to reload:', error);
    }
  };

  private renderErrorDetails() {
    const { error, errorInfo } = this.state;
    
    if (!error) return null;

    return (
      <ScrollView style={styles.errorDetails}>
        <Text style={styles.errorTitle}>Error Details</Text>
        <Text style={styles.errorText}>
          <Text style={styles.label}>Type: </Text>
          {error.name}
        </Text>
        <Text style={styles.errorText}>
          <Text style={styles.label}>Message: </Text>
          {error.message}
        </Text>
        {error.stack && (
          <Text style={styles.errorText}>
            <Text style={styles.label}>Stack: </Text>
            {error.stack.substring(0, 500)}...
          </Text>
        )}
        {errorInfo?.componentStack && (
          <Text style={styles.errorText}>
            <Text style={styles.label}>Component: </Text>
            {errorInfo.componentStack.substring(0, 300)}...
          </Text>
        )}
      </ScrollView>
    );
  }

  private renderFallbackUI() {
    const { error, retryCount } = this.state;
    const { level } = this.props;
    
    const canRetry = retryCount < this.maxRetries;
    const isCritical = level === 'critical';

    return (
      <View style={[styles.container, isCritical && styles.criticalContainer]}>
        <View style={styles.content}>
          <Text style={styles.title}>
            {isCritical ? '💥 Critical Error' : '⚠️ Something went wrong'}
          </Text>
          
          <Text style={styles.message}>
            {isCritical 
              ? 'A critical error occurred. Please restart the app.'
              : 'We encountered an unexpected error. This might be temporary.'
            }
          </Text>

          {error && (
            <Text style={styles.errorMessage}>
              {error.name}: {error.message}
            </Text>
          )}

          <View style={styles.actions}>
            {canRetry && !isCritical && (
              <TouchableOpacity 
                style={[styles.button, styles.primaryButton]} 
                onPress={this.handleRetry}
              >
                <Text style={styles.buttonText}>
                  Try Again ({this.maxRetries - retryCount} left)
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity 
              style={[styles.button, styles.secondaryButton]} 
              onPress={this.handleReload}
            >
              <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                {isCritical ? 'Restart App' : 'Reload'}
              </Text>
            </TouchableOpacity>
          </View>

          {__DEV__ && this.renderErrorDetails()}
        </View>
      </View>
    );
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return this.renderFallbackUI();
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: colors.background,
  },
  criticalContainer: {
    backgroundColor: '#ffebee',
  },
  content: {
    maxWidth: 400,
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 16,
    fontWeight: lightTheme.fontWeights.bold,
  },
  message: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 24,
    fontWeight: lightTheme.fontWeights.regular,
  },
  errorMessage: {
    fontSize: 14,
    color: colors.error,
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: lightTheme.fontWeights.medium,
    backgroundColor: '#ffebee',
    padding: 12,
    borderRadius: 8,
  },
  actions: {
    gap: 12,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: colors.primary,
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.border,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: lightTheme.fontWeights.semibold,
    color: '#FFFFFF',
  },
  secondaryButtonText: {
    color: colors.text,
  },
  errorDetails: {
    marginTop: 20,
    maxHeight: 200,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
    fontWeight: lightTheme.fontWeights.bold,
  },
  errorText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: 4,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  label: {
    fontWeight: 'bold',
    color: colors.text,
  },
});

// Higher-order component for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

export default ErrorBoundary;